import { useId, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoadingBlock, PageHeader } from '../ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { SettingsNavIcon } from '../settings-nav.tsx';
import SetupGuide from './setup-guide.tsx';
import {
  SETTINGS_GROUP_IDS,
  SETTINGS_SUBGROUP_IDS,
  useSettingsSectionContext,
  visibleSettingsSections,
  type SettingsGroupId,
  type SettingsSectionDef,
} from './settings-registry.tsx';
import { settingsLeafPath } from './settings-routes.ts';

/**
 * `/settings` as a hub: one link per section, grouped Essentials, Everyday
 * and Advanced, each opening that section on a page of its own.
 *
 * Nothing heavy mounts here. The long page painted every card at once, and
 * every card fetched on mount — Stripe, Twilio, API keys and the rest for
 * somebody who came to change a phone number. The hub reads only what it
 * needs to decide which links to show (the same two answers the long page's
 * rail needed) and the setup guide's progress.
 *
 * The search box filters the links by name and by the same hidden keyword
 * lists the long page's navigator searched, so "logo" or "iCal" still finds
 * its section.
 *
 * Advanced is split into subgroups, "Developers & data" among them, and the
 * web-only power features carry a one-line description: they are kept, all
 * of them, but contained where they do not compete with everyday settings.
 */
export default function SettingsHub() {
  const { t } = useTranslation();
  useDocumentTitle(t('settings.documentTitle'));
  const navigate = useNavigate();
  const { context } = useSettingsSectionContext();
  const [query, setQuery] = useState('');
  const headingId = useId();

  if (!context) return <LoadingBlock label={t('settings.loadingSettings')} />;

  const needle = query.trim().toLowerCase();
  const sections = visibleSettingsSections(context).filter(
    (def) =>
      needle === '' ||
      t(def.titleKey).toLowerCase().includes(needle) ||
      t(def.keywordsKey).toLowerCase().includes(needle),
  );
  const groups = SETTINGS_GROUP_IDS.map((id) => ({ id, sections: sections.filter((def) => def.group === id) })).filter(
    (group) => group.sections.length > 0,
  );

  const hint = (group: SettingsGroupId) =>
    group === 'essentials'
      ? t('settings.essentialsHint')
      : group === 'advanced'
        ? t('settings.hub.advancedHint')
        : null;

  return (
    <div data-settings-hub="">
      <PageHeader title={t('settings.title')} description={t('settings.description')} />

      <input
        type="search"
        className="field-control mb-6"
        placeholder={t('settings.nav.searchPlaceholder')}
        aria-label={t('settings.nav.searchLabel')}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      {needle ? null : <SetupGuide onJump={(id) => navigate(settingsLeafPath(id))} />}

      {groups.length === 0 ? <p className="field-hint">{t('settings.nav.noMatches')}</p> : null}

      {groups.map((group) => (
        <section key={group.id} className="mb-8" aria-labelledby={`${headingId}-${group.id}`}>
          <h2 id={`${headingId}-${group.id}`} className="mb-1 text-xl font-semibold">
            {t(`settings.nav.groups.${group.id}`)}
          </h2>
          {hint(group.id) ? (
            <p className="mb-3 text-sm text-[color:var(--color-muted)]">{hint(group.id)}</p>
          ) : (
            <div className="mb-3" />
          )}
          {group.id === 'advanced' ? (
            SETTINGS_SUBGROUP_IDS.map((subgroup) => {
              const members = group.sections.filter((def) => def.subgroup === subgroup);
              if (members.length === 0) return null;
              return (
                <div key={subgroup} className="mb-5">
                  <h3
                    id={`${headingId}-${subgroup}`}
                    className="mb-2 text-xs font-semibold tracking-wide text-[color:var(--color-muted)] uppercase"
                  >
                    {t(`settings.nav.groups.${subgroup}`)}
                  </h3>
                  <SettingsTiles sections={members} labelledBy={`${headingId}-${subgroup}`} />
                </div>
              );
            })
          ) : (
            <SettingsTiles sections={group.sections} />
          )}
        </section>
      ))}
    </div>
  );
}

function SettingsTiles({ sections, labelledBy }: { sections: SettingsSectionDef[]; labelledBy?: string }) {
  const { t } = useTranslation();
  return (
    <ul className="grid gap-2 sm:grid-cols-2" aria-labelledby={labelledBy}>
      {sections.map((def) => (
        <li key={def.id} className="min-w-0">
          <SettingsTile
            to={settingsLeafPath(def.id)}
            icon={<SettingsNavIcon name={def.id} />}
            label={t(def.titleKey)}
            description={def.descriptionKey ? t(def.descriptionKey) : null}
          />
        </li>
      ))}
    </ul>
  );
}

function SettingsTile({
  to,
  icon,
  label,
  description,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  description: string | null;
}) {
  return (
    <Link
      to={to}
      className="flex min-h-12 items-center gap-3 rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-4 py-3 text-sm text-[color:var(--color-ink)] hover:border-[color:var(--color-brand)] hover:bg-[color:var(--color-brand-soft)] hover:text-[color:var(--color-brand-strong)] focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      <span aria-hidden="true" className="shrink-0 text-[color:var(--color-brand)]">
        {icon}
      </span>
      <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
        <span className="block font-medium">{label}</span>
        {description ? (
          <span className="mt-0.5 block text-xs text-[color:var(--color-muted)]">{description}</span>
        ) : null}
      </span>
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-[color:var(--color-muted)]"
      >
        <path d="m9 6 6 6-6 6" />
      </svg>
    </Link>
  );
}
